import React, { useCallback } from 'react';
import { mergeStyleSets } from '@fluentui/react';
import { DownloadSimple, Table, FilePdf } from '@phosphor-icons/react';
import { boroondaraPalette } from '../../theme/boroondaraTheme';
import { SpatialLayer, GeoJSONFeatureCollection, GeoJSONFeature } from './types';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const p = boroondaraPalette;

interface ExportToolsProps {
  layers: SpatialLayer[];
  activeLayers: Set<string>;
  featureData: Record<string, GeoJSONFeatureCollection>;
  mapContainerRef: React.RefObject<HTMLDivElement>;
}

const styles = mergeStyleSets({
  toolbar: {
    position: 'absolute' as any,
    bottom: 12,
    right: 12,
    zIndex: 1000,
    display: 'flex',
    gap: 4,
  } as any,
  btn: {
    height: 32,
    padding: '0 10px',
    borderRadius: 4,
    border: `1px solid ${p.border}`,
    background: 'rgba(255,255,255,0.92)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 11,
    fontWeight: 500,
    color: p.text2,
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    selectors: { ':hover': { background: '#fff', borderColor: p.primary, color: p.primary } },
  } as any,
});

function downloadBlob(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ExportTools({ layers, activeLayers, featureData, mapContainerRef }: ExportToolsProps) {
  const handleGeoJSON = useCallback(() => {
    const allFeatures: GeoJSONFeature[] = [];
    layers.forEach((layer) => {
      if (!activeLayers.has(layer.layer_id)) return;
      const fc = featureData[layer.layer_id];
      if (fc?.features) allFeatures.push(...fc.features);
    });

    const collection = {
      type: 'FeatureCollection',
      features: allFeatures,
    };

    downloadBlob(
      JSON.stringify(collection, null, 2),
      `boroondara-spatial-${new Date().toISOString().slice(0, 10)}.geojson`,
      'application/geo+json'
    );
  }, [layers, activeLayers, featureData]);

  const handleCSV = useCallback(() => {
    const rows: Record<string, any>[] = [];
    layers.forEach((layer) => {
      if (!activeLayers.has(layer.layer_id)) return;
      const fc = featureData[layer.layer_id];
      if (!fc?.features) return;
      fc.features.forEach((f) => {
        const props = { ...f.properties };
        // Add lat/lng for point features
        if (f.geometry?.type === 'Point') {
          props.longitude = f.geometry.coordinates[0];
          props.latitude = f.geometry.coordinates[1];
        }
        rows.push(props);
      });
    });

    if (rows.length === 0) return;

    // Collect all unique keys
    const keys = new Set<string>();
    rows.forEach((r) => Object.keys(r).forEach((k) => keys.add(k)));
    const headers = Array.from(keys);

    const csvLines = [
      headers.join(','),
      ...rows.map((row) =>
        headers
          .map((h) => {
            const val = row[h];
            if (val === null || val === undefined) return '';
            const str = String(val);
            return str.includes(',') || str.includes('"') || str.includes('\n')
              ? `"${str.replace(/"/g, '""')}"`
              : str;
          })
          .join(',')
      ),
    ];

    downloadBlob(
      csvLines.join('\n'),
      `boroondara-spatial-${new Date().toISOString().slice(0, 10)}.csv`,
      'text/csv'
    );
  }, [layers, activeLayers, featureData]);

  const handlePDF = useCallback(async () => {
    const el = mapContainerRef.current;
    if (!el) return;

    try {
      const canvas = await html2canvas(el, {
        useCORS: true,
        allowTaint: true,
        scale: 2,
      });

      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();

      // Green header bar
      pdf.setFillColor(46, 135, 61);
      pdf.rect(0, 0, pageW, 16, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('City of Boroondara — Spatial Data Explorer', 10, 11);

      // Map image
      const imgData = canvas.toDataURL('image/png');
      const imgW = pageW - 20;
      const imgH = (canvas.height / canvas.width) * imgW;
      const maxImgH = pageH - 40;
      const finalH = Math.min(imgH, maxImgH);
      pdf.addImage(imgData, 'PNG', 10, 20, imgW, finalH);

      // Active layers legend
      const legendY = 20 + finalH + 6;
      if (legendY < pageH - 10) {
        pdf.setTextColor(72, 72, 72);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        const activeNames = layers
          .filter((l) => activeLayers.has(l.layer_id))
          .map((l) => l.name);
        pdf.text(`Active layers: ${activeNames.join(', ')}`, 10, legendY);
        pdf.text(
          `Generated: ${new Date().toLocaleDateString('en-AU')}`,
          10,
          legendY + 5
        );
      }

      pdf.save(`boroondara-map-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error('[SPATIAL] PDF export error:', err);
    }
  }, [layers, activeLayers, mapContainerRef]);

  return (
    <div className={styles.toolbar}>
      <button className={styles.btn} onClick={handleGeoJSON} title="Export as GeoJSON">
        <DownloadSimple size={12} weight="bold" />
        GeoJSON
      </button>
      <button className={styles.btn} onClick={handleCSV} title="Export as CSV">
        <Table size={12} weight="bold" />
        CSV
      </button>
      <button className={styles.btn} onClick={handlePDF} title="Print map as PDF">
        <FilePdf size={12} weight="bold" />
        PDF
      </button>
    </div>
  );
}
